import { PLACEHOLDER_AVATAR_PATH } from '@/constants/global'
import { APIError } from '@/plugins/api/error'
import { ApiSchemas, useV2Api } from '@/plugins/api/openapi'
import { PaymentMethod, PhysicianAddressAttendanceChannel } from '@/plugins/api/openapi/endpoints/physician-address'
import { useAlert } from '@/v2/composables/alert/alert'
import dayjs from 'dayjs'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useAuthStore } from './auth'

export type StateAbbreviation =
  | 'AC'
  | 'AL'
  | 'AP'
  | 'AM'
  | 'BA'
  | 'CE'
  | 'DF'
  | 'ES'
  | 'GO'
  | 'MA'
  | 'MT'
  | 'MS'
  | 'MG'
  | 'PA'
  | 'PB'
  | 'PR'
  | 'PE'
  | 'PI'
  | 'RJ'
  | 'RN'
  | 'RS'
  | 'RO'
  | 'RR'
  | 'SC'
  | 'SP'
  | 'SE'
  | 'TO'

//'MASCULINE' | 'FEMININE' | 'NO_ANSWER'
export const GenderMap = {
  MASCULINE: 'Masculino',
  FEMININE: 'Feminino',
  NO_ANSWER: null,
}
export type Crm = {
  stateId: StateAbbreviation | null
  value: string | null
}

export type Specialty = {
  rqe: string | null
  typeId: number | null
  typeName?: string
}

export type HealthPlan = {
  id: number
  name: string
}

export type Address = {
  id: number | null
  city: {
    id: number
    name: string
    stateId: StateAbbreviation
  } | null
  cep: string | null
  name: string | null
  district: string | null
  street: string | null
  number: string | null
  complement: string | null
  cnes: string | null
  phone: string | null
  recievesPatient: boolean
  attendanceChannels: PhysicianAddressAttendanceChannel[]
  attendancePrice: number | null
  discountPrice: number | null
  healthPlans: HealthPlan[]
  paymentMethods: PaymentMethod[]
  active: boolean
}

export type RemoteAddress = {
  id: number | null
  phone: string | null
  attendancePrice: number | null
  discountPrice: number | null
  healthPlans: HealthPlan[]
  paymentMethods: PaymentMethod[]
  active: boolean
}

export type AppointmentType = 'Remote' | 'InPerson'
export type PatientAgeRange =
  | 'LessThan3'
  | 'Between3And12'
  | 'Between13And17'
  | 'Between18And59'
  | 'GreaterThanOrEqualTo60'
export type ImmunobiologicalExperience = 'Never' | 'Between1And5' | 'Between6And10' | 'MoreThan10'

export type Physician = {
  id: number | null
  name: string | null
  about: string | null
  cns: string | null
  cpf: string | null
  birthday: string | null
  gender: keyof typeof GenderMap | null
  phone: string | null
  hasWhatsapp: boolean
  isBiologicalExpert: boolean
  email: string | null
  isTester: boolean
  appointmentTypes: AppointmentType[]
  highlights: string[]
  remoteAddress?: RemoteAddress
  patientAgeRange: PatientAgeRange[]
  immunobiologicalExperience: ImmunobiologicalExperience | null
  graduation: string | null
  graduationYear: number | null
  completeness: number | null
  findYourDoctorDiseases: { id: number; name: string }[]
  findYourDoctorMedicines: { id: number; name: string }[]
}
const defaultPhysicianValue: Physician = {
  id: null,
  about: null,
  birthday: null,
  gender: null,
  cns: null,
  cpf: null,
  email: null,
  isTester: false,
  hasWhatsapp: false,
  isBiologicalExpert: false,
  name: null,
  phone: null,
  appointmentTypes: [],
  highlights: [],
  patientAgeRange: [],
  immunobiologicalExperience: null,
  graduation: null,
  graduationYear: null,
  completeness: null,
  findYourDoctorDiseases: [],
  findYourDoctorMedicines: [],
}

export type MedicalDocumentTemplateType = 'REPORT' | 'RECEIPT'
export type PhysicianTemplates = {
  id: number
  name: string
  type: MedicalDocumentTemplateType
}

export const usePhysicianStore = defineStore('physician', () => {
  const api = useV2Api()
  const alert = useAlert()
  const authStore = useAuthStore()

  const avatarUrl = ref<string>(PLACEHOLDER_AVATAR_PATH)
  const physician = ref<Physician>(defaultPhysicianValue)
  const physicianCrms = ref<Crm[]>([
    {
      stateId: null,
      value: null,
    },
  ])
  const physicianSpecialties = ref<Specialty[]>([])
  const physicianTemplates = ref<PhysicianTemplates[]>([])

  const physicianCurrentAddresses = ref<Address[]>([])
  const physicianRemovedAddresses = ref<Address[]>([])

  const physicianInterestedDiseases = ref({
    selected: [] as ApiSchemas['InterestedDiseasePresenter'][],
    other: null as ApiSchemas['OtherInterestedDiseasePresenter'] | null,
  })

  const extensionSubscriptions = ref<ApiSchemas['ExtensionSubscriptionPresenter'][]>([])

  function loadAddresses(addresses: ApiSchemas['PhysicianAddressPresenter'][]) {
    const allAddresses = (addresses || []).map((address) => ({
      ...address,
      city: address.city
        ? {
            id: address.city.id,
            name: address.city.name,
            stateId: address.city.stateId,
          }
        : null,
      healthPlans: (address.healthPlans || []).map((hp) => ({
        id: hp.id,
        name: hp.name,
      })),
      recievesPatient: address.recievesPatient ?? false,
    }))

    physicianCurrentAddresses.value = allAddresses.filter((address) => address.active)
    physicianRemovedAddresses.value = allAddresses.filter((address) => !address.active)
  }

  function currencyToNumber(currency: string) {
    const unformattedCurrency = currency.replace('R$', '').trim().replaceAll('.', '').replace(',', '.')

    return Number(unformattedCurrency)
  }

  function numberToCurrency(number: number) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number)
  }

  const hasChanges = ref(false)
  async function loadPhysicianData() {
    try {
      const {
        addresses,
        documentTemplates,
        user,
        attendances,
        specialties,
        crms,
        interestedDiseases,
        otherInterestedDisease,
        avatar,
        ...physicianData
      } = await api.physician.get(authStore.userId!)

      physician.value = {
        ...physicianData,
        cpf: !!physicianData.cpf ? physicianData.cpf?.trim() : null,
        email: user!.email,
        isTester: user!.isTester,
        birthday: dayjs(physicianData.birthday, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        isBiologicalExpert: physicianData.isBiologicalExpert,
        highlights: physicianData.highlights,
        findYourDoctorDiseases: physicianData.findYourDoctorDiseases ?? [],
        findYourDoctorMedicines: physicianData.findYourDoctorMedicines ?? [],
        remoteAddress: physicianData.remoteAddress
          ? {
              id: physicianData.remoteAddress.id,
              phone: physicianData.remoteAddress.phone,
              attendancePrice: physicianData.remoteAddress.attendancePrice,
              discountPrice: physicianData.remoteAddress.discountPrice,
              healthPlans: physicianData.remoteAddress.healthPlans ?? [],
              paymentMethods: physicianData.remoteAddress.paymentMethods,
              active: physicianData.remoteAddress.active,
            }
          : undefined,
      }
      avatarUrl.value = avatar?.url ?? PLACEHOLDER_AVATAR_PATH
      physicianCrms.value = (crms || []).map((crm) => ({
        stateId: crm.stateId,
        value: crm.value,
      }))

      physicianSpecialties.value = (specialties || []).map((specialty) => ({
        rqe: specialty.rqe,
        typeId: specialty.typeId,
        typeName: specialty.type?.name,
      }))

      extensionSubscriptions.value = user?.extensionSubscriptions ?? []

      loadAddresses(addresses ?? [])

      physicianTemplates.value = (documentTemplates || []).map((docTemplate) => ({
        ...docTemplate,
      }))

      if (interestedDiseases) {
        physicianInterestedDiseases.value.selected = interestedDiseases
      }

      if (otherInterestedDisease) {
        physicianInterestedDiseases.value.other = otherInterestedDisease
      }

      hasChanges.value = false
    } catch (error) {
      alert.error({ error })
    }
  }

  async function updatePhysician(withAlert: boolean = true) {
    try {
      if (physician.value.id) {
        const payload: ApiSchemas['UpdatePhysicianDto'] = {
          name: physician.value.name!,
          about: physician.value.about || undefined,
          phone: physician.value.phone!,
          cns: physician.value.cns,
          cpf: physician.value.cpf?.trim().length ? physician.value.cpf.trim() : undefined,
          email: physician.value.email!,
          isTester: physician.value.isTester,
          gender: physician.value.gender,
          birthday: physician.value.birthday ? dayjs(physician.value.birthday, 'DD/MM/YYYY').toISOString() : null,
          hasWhatsapp: physician.value.hasWhatsapp,
          isBiologicalExpert: physician.value.isBiologicalExpert,
          immunobiologicalExperience: physician.value.immunobiologicalExperience ?? undefined,
          patientAgeRange: physician.value.patientAgeRange,
          crms: physicianCrms.value.filter(
            (crm): crm is { stateId: StateAbbreviation; value: string } => crm.stateId !== null && crm.value !== null,
          ),
          specialties: physicianSpecialties.value.filter(
            (specialty): specialty is { rqe: string | null; typeId: number } => specialty.typeId !== null,
          ),
          addresses: physicianCurrentAddresses.value.map((physicianAddress) => ({
            id: physicianAddress.id,
            attendanceChannels: physicianAddress.attendanceChannels ?? [],
            attendancePrice: physicianAddress.attendancePrice ?? undefined,
            discountPrice:
              authStore.isAdmin && physicianAddress.discountPrice ? physicianAddress.discountPrice : undefined,
            cnes: physicianAddress.cnes ?? undefined,
            complement: physicianAddress.complement ?? undefined,
            healthPlanIds: physicianAddress.healthPlans.map(({ id }) => id),
            phone: physicianAddress.phone!,
            cep: physicianAddress.cep!,
            street: physicianAddress.street!,
            cityId: physicianAddress.city!.id,
            district: physicianAddress.district!,
            number: physicianAddress.number!,
            paymentMethods: physicianAddress.paymentMethods,
            name: physicianAddress.name ?? undefined,
            recievesPatient: physicianAddress.recievesPatient,
          })),
          appointmentTypes: physician.value.appointmentTypes,
          highlights: physician.value.highlights,
          remoteAddress: physician.value.remoteAddress
            ? {
                id: physician.value.remoteAddress.id,
                phone: physician.value.remoteAddress.phone!,
                attendancePrice: physician.value.remoteAddress.attendancePrice!,
                discountPrice: physician.value.remoteAddress.discountPrice ?? null,
                healthPlanIds: physician.value.remoteAddress.healthPlans.map((hp) => hp.id),
              }
            : undefined,
          interestedDiseases: physicianInterestedDiseases.value.selected.map((item) => item.id),
          otherInterestedDisease: physicianInterestedDiseases.value.other?.name ?? null,
        }

        await api.physician.update(physician.value.id, payload)
        if (withAlert) {
          alert.ok({
            message: 'Perfil atualizado',
            description: 'Seus dados foram atualizados com sucesso!',
          })
        }

        hasChanges.value = false
        return true
      }
    } catch (error) {
      if (error instanceof APIError && error.name === 'ValidationException') {
        alert.error({
          error,
          message: 'Falha ao validar os dados. Por favor, tente novamente!',
          description: 'Verifique se as informações estão corretas.',
        })
      } else if (error instanceof APIError && error.name === 'PhysicianWithCpfAlreadyExists') {
        alert.error({
          error,
          message: 'Não foi possível atualizar o CPF',
          description:
            'Este CPF já está vinculado a outra conta. Entre em contato com o suporte para resolver o problema',
          action: {
            text: 'Falar com suporte',
            fn: () => {
              const url = `https://api.whatsapp.com/send?phone=5511998933332&text=Ol%C3%A1%20me%20chamo%20${encodeURI(physician.value.name ?? '')},%20estou%20na%20plataforma%20Bioplanner%20e%20preciso%20de%20ajuda.&lang=pt_br`
              window.open(url, '_blank')
            },
          },
        })
      } else {
        alert.error({ error })
      }
      return false
    }
  }

  function resetValues() {
    physician.value = defaultPhysicianValue
    physicianCurrentAddresses.value = []
    physicianCrms.value = [
      {
        stateId: null,
        value: null,
      },
    ]
    physicianSpecialties.value = []
    physicianTemplates.value = []
    avatarUrl.value = PLACEHOLDER_AVATAR_PATH
  }

  watch(
    () => authStore.isLogged,
    async () => {
      if (authStore.isLogged && !authStore.isAdmin) {
        await loadPhysicianData()
      } else {
        resetValues()
      }
    },
    {
      immediate: true,
    },
  )

  watch(
    [
      physician,
      physicianCrms,
      physicianSpecialties,
      physicianCurrentAddresses,
      physicianInterestedDiseases,
      extensionSubscriptions,
    ],
    () => {
      hasChanges.value = true
    },
    { deep: true },
  )
  //TODO verificar funcionamento do reloadPhysicianData (hasChanges)
  async function reloadPhysicianData(force = false) {
    if (!authStore.isAdmin && (hasChanges.value || force)) {
      await loadPhysicianData()
    }
  }

  return {
    avatarUrl,
    physician,
    specialties: physicianSpecialties,
    extensionSubscriptions,
    crms: physicianCrms,
    addresses: physicianCurrentAddresses,
    removedAddresses: physicianRemovedAddresses,
    templates: physicianTemplates,
    interestedDiseases: physicianInterestedDiseases,
    reloadPhysicianData,
    updatePhysician,
    currencyToNumber,
    numberToCurrency,
  }
})
